import { BP_ROLE_TYPE } from 'config/BusinessPartnerConsts'
import React, { useMemo, useState } from 'react'
import { SparkUser } from 'types/userProfile'

export const PROD_TECH_ADMIN_ROLE = 'APP-PLM-TARGETTECHADMIN'
export const NON_PROD_TECH_ADMIN_ROLE = 'APP-PLM-TARGETTECHADMIN-NP'
export const PROD_PRODUCT_TEAM_ADMIN_ROLE = 'APP-PLM-PRODUCTTEAMADMIN'
export const NON_PROD_PRODUCT_TEAM_ADMIN_ROLE = 'APP-PLM-PRODUCTTEAMADMIN-NP'

export const UserContext = React.createContext<{
  sparkUser: SparkUser | null
  setSparkUser: React.Dispatch<React.SetStateAction<SparkUser | null>>
  isInternal: boolean
  isPackagingSupplier: boolean
  isFabricSupplier: boolean
  isTrimSupplier: boolean
  isAdmin: boolean
  canImpersonate: boolean
  isDark: boolean
}>({
  sparkUser: null,
  setSparkUser: (user: any) => {},
  isInternal: false,
  isPackagingSupplier: false,
  isFabricSupplier: false,
  isTrimSupplier: false,
  isAdmin: false,
  canImpersonate: false,
  isDark: false,
})

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [sparkUser, setSparkUser] = useState<SparkUser | null>(null)

  const isInternal = useMemo(() => {
    return sparkUser?.internal ?? false
  }, [sparkUser])

  const isPackagingSupplier = useMemo(() => {
    return sparkUser?.currentVMMRole === BP_ROLE_TYPE.PACKAGING_SUPPLIER
  }, [sparkUser])

  const isFabricSupplier = useMemo(() => {
    return sparkUser?.currentVMMRole === BP_ROLE_TYPE.FABRIC_SUPPLIER
  }, [sparkUser])

  const isTrimSupplier = useMemo(() => {
    return sparkUser?.currentVMMRole === BP_ROLE_TYPE.TRIM_SUPPLIER
  }, [sparkUser])

  const isAdmin = useMemo(() => {
    // Transform the roles to lowercase to ensure case-insensitive comparison
    const transformedRoles = sparkUser?.roles.map((role) => role.toLowerCase())
    return transformedRoles?.includes('admin') ?? false
  }, [sparkUser])

  const canImpersonate = useMemo(() => {
    // Transform the roles to lowercase to ensure case-insensitive comparison
    const transformedRoles = sparkUser?.roles.map((role) => role.toLowerCase())
    return transformedRoles?.includes('sparkimpersonation') ?? false
  }, [sparkUser])

  const isDark = useMemo(
    () =>
      sparkUser?.theme === 'DARK' ||
      (sparkUser?.theme !== 'LIGHT' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches),
    [sparkUser?.theme],
  )

  const contextValue = useMemo(
    () => ({
      sparkUser,
      setSparkUser,
      isInternal,
      isPackagingSupplier,
      isFabricSupplier,
      isTrimSupplier,
      isAdmin,
      canImpersonate,
      isDark,
    }),
    [
      isFabricSupplier,
      isInternal,
      isPackagingSupplier,
      isTrimSupplier,
      sparkUser,
      isAdmin,
      canImpersonate,
      isDark,
    ],
  )

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  )
}
